/* =========================================================================
   useDashboardActions — custom hook for dashboard handlers (widget creation,
   workspace tab context menu).
   ========================================================================= */

import React, { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CAT, getSubParams, makeColumns, GapZone, moveTo, ico } from '../../../../../CommonComponents/telemetry';
import { TelemetryPanel, Workspace, MenuItem } from '../lib/dashboard-types';
import useTelemetryConfirm from './useTelemetryConfirm';
import { showToastMessage } from '../../../../../../utils/commonFunctions';

interface UseDashboardActionsProps {
  activeLayout: TelemetryPanel[];
  setLayout: (layout: TelemetryPanel[]) => void;
  dashboards: Workspace[];
  openMenu: (e: React.MouseEvent, items: MenuItem[], opts?: any) => void;
  closeMenu: () => void;
  renameWorkspace: (id: string, name: string) => void;
  duplicateWorkspace: (id: string) => void;
  deleteWorkspace: (id: string) => void;
  setInsertGap: (gap: GapZone | null) => void;
  insertGap: GapZone | null;
}

export function useDashboardActions({
  activeLayout,
  setLayout,
  dashboards,
  openMenu,
  closeMenu,
  renameWorkspace,
  duplicateWorkspace,
  deleteWorkspace,
  setInsertGap,
  insertGap,
}: UseDashboardActionsProps) {
  const { confirmWorkspaceDeletion } = useTelemetryConfirm();

  // Handle add widget to grid
  const handleAddWidget = useCallback(
    (sub: string, tel: string, viz: string) => {
      const maxY = activeLayout.reduce((m, p) => Math.max(m, p.y + p.h), 0);
      const channelDef = getSubParams(sub)[tel];
      if (!channelDef) return;

      const sizeMap: Record<string, { w: number; h: number }> = {
        line: { w: 4, h: 3 }, area: { w: 4, h: 3 },
        gauge: { w: 3, h: 4 }, radial: { w: 3, h: 4 },
        kpi: { w: 4, h: 2 }, pie: { w: 3, h: 4 },
        bar: { w: 4, h: 3 }, hbar: { w: 4, h: 3 },
        radar: { w: 4, h: 4 }, table: { w: 4, h: 3 },
        ctable: { w: 6, h: 4 }, cchart: { w: 6, h: 4 },
      };
      const sz = sizeMap[viz] || { w: 4, h: 3 };

      let insertX = 0;
      let insertY = maxY;
      let insertW = sz.w;
      let insertH = sz.h;

      if (insertGap) {
        if (insertGap.isSlot) {
          insertX = insertGap.x ?? 0;
          insertY = insertGap.y ?? 0;
          insertW = Math.max(2, insertGap.panelW ?? sz.w);
          insertH = Math.max(2, insertGap.panelH ?? sz.h);
        } else if (insertGap.orient === 'v') {
          if (insertGap.isLeft) {
            insertW = insertGap.panelW ?? sz.w;
            insertH = insertGap.panelH ?? sz.h;
            insertX = Math.max(0, (insertGap.x ?? 0) - insertW);
            insertY = insertGap.yStart ?? 0;
          } else {
            insertX = insertGap.x ?? 0;
            insertY = insertGap.yStart ?? 0;
            insertW = insertGap.panelW ?? sz.w;
            insertH = insertGap.panelH ?? sz.h;
          }
        } else {
          insertX = insertGap.xStart ?? 0;
          insertY = insertGap.y ?? 0;
          insertW = insertGap.panelW ?? sz.w;
          insertH = insertGap.panelH ?? sz.h;
        }
      }

      const newPanel: TelemetryPanel = {
        id: uuidv4(),
        graphMode: viz,
        name: channelDef.label || CAT[sub]?.label || tel,
        x: insertX,
        y: insertY,
        w: insertW,
        h: insertH,
        minW: 2,
        minH: 2,
        parameters: makeColumns(sub, tel),
        ...(viz === 'ctable' || viz === 'cchart' ? {
          hiddenCols: [],
          search: '',
          orient: 'rows',
          order: 'desc',
        } : {}),
      };

      const newLayout = [...activeLayout, newPanel];
      setLayout(moveTo(newLayout, newPanel.id, insertX, insertY));
      setInsertGap(null);
    },
    [activeLayout, setLayout, insertGap, setInsertGap]
  );

  // Workspace tab context menu handler
  const handleTabContextMenu = useCallback(
    (e: React.MouseEvent, dbId: string) => {
      e.preventDefault();
      const db = dashboards.find((d) => d.id === dbId);
      if (!db) return;
      openMenu(
        e,
        [
          {
            label: 'Rename',
            icon: ico('edit'),
            onClick: () => {
              const newName = window.prompt('Rename workspace:', db.name);
              if (newName && newName.trim()) renameWorkspace(dbId, newName.trim());
              closeMenu();
            },
          },
          {
            label: 'Duplicate',
            icon: ico('copy'),
            onClick: () => {
              duplicateWorkspace(dbId);
              closeMenu();
            },
          },
          {
            label: 'Save',
            icon: ico('save'),
            onClick: () => {
              showToastMessage('success', 'Workspace saved');
              closeMenu();
            },
          },
          {
            label: 'Delete',
            icon: ico('trash'),
            danger: true,
            disabled: dashboards.length <= 1,
            onClick: () => {
              confirmWorkspaceDeletion(db.name || 'Workspace', () => deleteWorkspace(dbId));
              closeMenu();
            },
          },
        ],
        { atPointer: true, width: 180 }
      );
    },
    [dashboards, openMenu, closeMenu, renameWorkspace, duplicateWorkspace, deleteWorkspace, confirmWorkspaceDeletion]
  );

  return {
    handleAddWidget,
    handleTabContextMenu,
  };
}

export default useDashboardActions;
